import { useState } from 'react'
import { useFetchData } from '../../hooks/useFetchData'
import { autoScoreTAT } from '../../utils/evaluator'
import { useAutoSave } from '../../hooks/useDebouncedSave'
import { Loader, AlertCircle, Save, Image as ImageIcon } from 'lucide-react'

export default function TATPractice({ state, updateState }) {
  const { data, loading, error } = useFetchData('/data/tat_images.json')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [story, setStory] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [evaluation, setEvaluation] = useState(null)

  const images = data?.images || []
  const currentImage = images[currentIndex]

  // Auto-save story to drafts
  useAutoSave(
    story,
    (text) => {
      if (currentImage && text) {
        updateState(`drafts.tat.${currentImage.id}`, text)
      }
    },
    2000
  )

  const handleSubmit = () => {
    if (!story.trim()) return

    const result = autoScoreTAT(story, '', currentImage.rubric)
    setEvaluation(result)
    setSubmitted(true)

    // Save to progress
    updateState('progress.tat.stories', [
      ...(state.progress.tat?.stories || []),
      {
        imageId: currentImage.id,
        story,
        score: result.score,
        timestamp: new Date().toISOString(),
      },
    ])
    updateState('progress.tat.totalStories', (state.progress.tat?.totalStories || 0) + 1)
  }

  const handleNext = () => {
    setStory('')
    setSubmitted(false)
    setEvaluation(null)
    setCurrentIndex((currentIndex + 1) % images.length)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading images...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Images</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!currentImage) return null

  const wordCount = story.trim().split(/\s+/).filter((w) => w.length > 0).length

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">TAT Practice</h2>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Image {currentIndex + 1} of {images.length}
        </div>
      </div>

      <div className="card bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
        <h3 className="font-bold mb-2">Instructions</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Look at the image and write a story in 100-150 words. Include: Situation, Task, Action, and Result (STAR format).
          Focus on positive themes and officer-like qualities.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Image Section */}
        <div className="card">
          <h3 className="font-semibold mb-3">{currentImage.title}</h3>
          <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
            {currentImage.thumbnail ? (
              <img
                src={currentImage.thumbnail}
                alt={currentImage.title}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'flex'
                }}
              />
            ) : null}
            <div className="flex flex-col items-center text-gray-500" style={{ display: currentImage.thumbnail ? 'none' : 'flex' }}>
              <ImageIcon className="w-16 h-16 mb-2" />
              <p className="text-sm">Image placeholder</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {currentImage.description}
          </p>
        </div>

        {/* Story Writing Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">Your Story</h3>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Save className="w-4 h-4 mr-1" />
              <span>{wordCount} words</span>
            </div>
          </div>

          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            placeholder="Write your story here... Remember to include Situation, Task, Action, and Result."
            className="textarea-field"
            rows="12"
            disabled={submitted}
          />

          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={!story.trim()}
              className="btn-primary w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Story
            </button>
          )}
        </div>
      </div>

      {/* Evaluation */}
      {submitted && evaluation && (
        <div className="card bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 animate-slide-in">
          <h3 className="font-bold text-lg mb-3">Evaluation</h3>
          
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold">Score</span>
              <span className="text-2xl font-bold text-green-600">
                {evaluation.score}/100
              </span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill bg-green-600" style={{ width: `${evaluation.score}%` }} />
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold mb-2">Feedback:</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{evaluation.feedback}</p>
          </div>

          {evaluation.suggestions.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold mb-2">Suggestions:</p>
              <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {evaluation.suggestions.map((suggestion, index) => (
                  <li key={index}>• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {evaluation.details && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-2 bg-white dark:bg-gray-700 rounded">
                <span className="text-gray-600 dark:text-gray-400">Word Count:</span>
                <span className="font-semibold ml-2">{evaluation.details.wordCount}</span>
              </div>
              <div className="p-2 bg-white dark:bg-gray-700 rounded">
                <span className="text-gray-600 dark:text-gray-400">Sentences:</span>
                <span className="font-semibold ml-2">{evaluation.details.sentenceCount}</span>
              </div>
            </div>
          )}

          <button onClick={handleNext} className="btn-primary w-full mt-4">
            Next Image
          </button>
        </div>
      )}
    </div>
  )
}
